import { useState, useEffect } from "react";
import useFetchWithMsal from "../useFetchWithMsal";
import { protectedResources } from "../../authConfig";

/**
 * Custom hook for managing Obligo financial data
 * Handles fetching customer financial information, obligo details, and related data
 */
export const useObligoData = (custId, allBranches) => {
  const [data, setData] = useState({
    obligo: null,
    obligoPerBranch: null,
    fnccust: null,
    openOrder: null,
    transPerBranch: null,
    invoicesPerBranch: null,
    obligoValue: null,
    openInvoices: null,
    delayOrders: null,
    openTransaction: null,
    collateralDetails: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  /**
   * Fetches customer company details (FNCCUST)
   */
  const fetchFnccust = async () => {
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/collection/fnccust/${custId}`,
        { allBranchesList: allBranches }
      );
      
      setData(prev => ({
        ...prev,
        fnccust: response,
        collateralDetails: response?.NEO_CUSTINSURACE_SUBFORM || null
      }));
    } catch (error) {
      setError(true);
      
      if (error.status === 404) {
        setSnackbar({ open: true, message: "הלקוח לא קיים במערכת" });
      } else if (error.status === 403) {
        setSnackbar({ open: true, message: "אין לך הרשאות מתאימות" });
      }
      
      setData(prev => ({ ...prev, fnccust: null }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches obligo (credit limit) details
   */
  const fetchObligo = async () => {
    try {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/collection/obligo/${custId}`,
        { allBranchesList: allBranches }
      );
      
      const obligoData = response.value;
      const obligoValue = obligoData?.[0] || null;
      
      // Filter open invoices (חל = invoice, חלמ = credit memo)
      const openInvoices = obligoValue?.OBLIGO_FNCITEMS_SUBFORM?.filter(
        (item) => item.FNCPATNAME === "חל" || item.FNCPATNAME === "חלמ"
      ) || [];
      
      setData(prev => ({
        ...prev,
        obligo: obligoData,
        obligoValue: obligoValue,
        openOrder: obligoValue?.OBLIGO_ORDERS_SUBFORM || null,
        openInvoices: openInvoices,
        openTransaction: obligoValue?.OBLIGO_FNCITEMS_SUBFORM || []
      }));
    } catch (error) {
      if (error.status === 403) {
        setSnackbar({ open: true, message: "אין לך הרשאות מתאימות" });
      }
      
      setData(prev => ({
        ...prev,
        obligo: null,
        openTransaction: null,
        openInvoices: null,
        openOrder: null
      }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches obligo details per branch
   */
  const fetchObligoPerBranch = async () => {
    try {
      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/collection/obligoPerBranch/${custId}`
      );
      
      // Filter invoices per branch
      const invoicesPerBranch = response.transPerBranch?.filter(
        item => item.FNCPATNAME === "חל" || item.FNCPATNAME === "חלמ"
      ) || [];
      
      setData(prev => ({
        ...prev,
        obligoPerBranch: response.obligoPerBranch,
        transPerBranch: response.transPerBranch,
        invoicesPerBranch: invoicesPerBranch
      }));
    } catch (error) {
      if (error.status === 403) {
        setSnackbar({ open: true, message: "אין לך הרשאות מתאימות" });
      }
      
      setData(prev => ({
        ...prev,
        obligoPerBranch: null,
        transPerBranch: null,
        invoicesPerBranch: null
      }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches open orders and filters delayed orders
   */
  const fetchOpenOrder = async () => {
    try {
      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/financialData/collection/openOrder/${custId}`
      );
      
      // Filter orders that exceed obligo and are not confirmed
      const delayedOrders = response.value?.filter(
        (item) => item.NEO_OBLIGOEXIST === "Y" && item.NEO_OBLIGOCONFIRM !== "Y"
      ) || [];
      
      setData(prev => ({
        ...prev,
        delayOrders: delayedOrders
      }));
    } catch (error) {
      if (error.status === 403) {
        setSnackbar({ open: true, message: "אין לך הרשאות מתאימות" });
      }
      
      setData(prev => ({ ...prev, delayOrders: null }));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches all customer financial data
   */
  const fetchAllData = () => {
    setError(false);
    setLoading(true);
    
    // Reset data
    setData({
      obligo: null,
      obligoPerBranch: null,
      fnccust: null,
      openOrder: null,
      transPerBranch: null,
      invoicesPerBranch: null,
      obligoValue: null,
      openInvoices: null,
      delayOrders: null,
      openTransaction: null,
      collateralDetails: null,
    });

    // Fetch all data in parallel
    Promise.all([
      fetchFnccust(),
      fetchObligo(),
      fetchObligoPerBranch(),
      fetchOpenOrder()
    ]);
  };

  /**
   * Close snackbar notification
   */
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return {
    data,
    loading,
    error,
    snackbar,
    fetchAllData,
    closeSnackbar
  };
};
